/**
 * Hook React du shift gate Conversations (2026-06-20).
 *
 * Combine :
 *   - la query Convex `shiftGate.getGateForCurrentUser` (réactive),
 *   - un tick local toutes les 30 s pour rafraîchir les compteurs « commence
 *     dans X » sans re-query Convex (compteurs purs côté client),
 *   - la math pure `computeGateState` qui synthétise les deux en `GateState`.
 *
 * L'accountId vient du parent ConversationsPanel (résolu via la liste des
 * accounts du caller). Tant qu'il n'est pas connu, le hook renvoie `loading`.
 *
 * Pourquoi un tick séparé : la query Convex est réactive aux ÉCRITURES (un
 * autre user ajoute un shift, le caller Start, le cron Lot 3 close). Mais le
 * passage du temps n'est PAS une écriture — sans tick local, la transition
 * « j'étais 1 min avant le shift → maintenant je suis dedans » ne déclenche
 * aucun re-render. Le tick force le recompute toutes les 30 s. Pause en
 * onglet caché (cf ConversationsPanel) — cohérent avec le tick `now` existant.
 */
import { useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { useMutation, useQuery } from "convex/react";

import { api } from "@/../convex/_generated/api";
import type { Id } from "@/../convex/_generated/dataModel";
import { useModeDemo } from "@/core/demo";
import { etatDeLaPorte, shiftDemoAtom } from "@/core/demo/shift";

import {
  computeGateState,
  type AbsencePrevenue,
  type GateState,
  type ShiftBlock,
} from "./shiftGateMath";

/** Périodicité du tick local. 30 s = pareil que le tick `now` existant du
 *  panneau Conversations — précision suffisante pour des compteurs « 12 min,
 *  11 min, 10 min… » et coût CPU nul. */
const GATE_TICK_MS = 30_000;

/** Cadence de remesure de l'horloge serveur. Une mutation vide toutes les
 *  cinq minutes : négligeable, et c'est le délai maximal pendant lequel une
 *  horloge de poste qui vient de sauter fausse encore les heures affichées. */
const HORLOGE_REMESURE_MS = 5 * 60_000;

export interface ShiftGateHookResult {
  /** True tant qu'on n'a pas reçu la première réponse Convex. */
  loading: boolean;
  /** État synthétisé (cf `GateState`) — null pendant le loading. */
  state: GateState | null;
  /** Instant courant utilisé pour le calcul — exposé pour les compteurs UI. */
  now: number;
  /**
   * Ecart mesure entre l’horloge de CETTE machine et celle du serveur, en ms
   * (positif = la machine RETARDE). `0` tant qu’on n’a pas mesure, et `0` si le
   * serveur est injoignable — jamais une valeur devinee.
   *
   * Expose parce qu’un ecart n’est pas un detail interne : au-dela de la
   * tolerance serveur il fausse TOUTES les heures que la personne voit, et
   * l’ecran doit pouvoir le lui dire plutot que de la laisser dehors sans
   * raison — c’est tres exactement la panne du 09-09-2026.
   */
  decalageHorlogeMs: number;
  /**
   * Une session de shift est-elle OUVERTE côté serveur — clock-in fait,
   * clock-out pas encore ? C'est `activeSession` de Convex, tel quel : la
   * vérité du serveur, sans aucune horloge de poste dedans.
   *
   * Distinct de `state.kind === "active"`, qui ajoute « et l'heure de CETTE
   * machine (corrigée) est avant la fin prévue ». La présence des chatteurs
   * (`useChatterPresence`) lit CE champ : le 09-09-2026, une horloge de poste
   * a fait sortir une chatteuse de `active` une heure avant la fin de sa
   * session, et sa présence avec — alors que le serveur la tenait bien pour
   * pointée. C'est le serveur qui ferme les sessions (cron) ; c'est lui qu'on
   * écoute. `false` pendant le loading et en démo.
   */
  sessionOuverte: boolean;
  /** La session ouverte est-elle en pause (geste « Pause » du shift) ? */
  enPauseDeShift: boolean;
  /**
   * Le caller a-t-il au moins UN bloc de shift à son nom ? Question distincte
   * de `state` : `computeGateState` répond sur la FENÊTRE (suis-je dedans,
   * ai-je cliqué Start) et rend `free` — donc « autorisé » — dès que l'org
   * n'a planifié aucun shift, ce qui écrase l'information. Le kiosk chatteur
   * a besoin de la question brute pour décider si l'onglet Conversations
   * existe (cf `buildChatterKiosk`). `false` pendant le loading.
   */
  hasAssignedShift: boolean;
  /** Ses absences prévenues à venir (`absencesPrevenues`). `[]` pendant le
   *  loading, en démo, et face à un serveur qui ne les rend pas encore. */
  absences: readonly AbsencePrevenue[];
}

const AUCUNE_ABSENCE: readonly AbsencePrevenue[] = [];

export function useShiftGate(
  accountId: Id<"accounts"> | null,
): ShiftGateHookResult {
  // 🎬 Mode démo : le shift vit dans un atom, pas dans Convex. Kayla est
  // patronne — en vrai elle est en `bypass` et ne voit JAMAIS cette porte,
  // donc c'est le seul moyen de filmer « je démarre mon shift ». Et surtout :
  // une démo ne doit pas ouvrir une vraie session de shift dans la base d'une
  // agence, elle compterait dans les statistiques.
  const enDemo = useModeDemo();
  const shiftDemo = useAtomValue(shiftDemoAtom);
  // Le seul morceau du créneau de démo qui ne s'invente pas : le salon vocal.
  // On lit celui du serveur Discord réel plutôt que d'en recopier l'id ici
  // (cf `core/demo/shift.ts`). `undefined` pendant le vol → `null` : l'écran
  // annonce l'exigence sans pointer un salon, exactement comme en prod quand
  // aucun salon ne porte les bornes du créneau.
  const lienVocalDemo = useQuery(
    api.shiftGate.lienVocalDeDemonstration,
    accountId && enDemo ? { accountId } : "skip",
  );

  // `useQuery` accepte « skip » via un argument null ; en pratique on passe
  // l'arg seulement si accountId est connu (sinon on saute la query).
  const gate = useQuery(
    api.shiftGate.getGateForCurrentUser,
    accountId && !enDemo ? { accountId } : "skip",
  );

  // Ecart avec l’horloge du serveur. Tant qu’on n’a pas mesure il vaut 0 —
  // c’est-a-dire exactement le comportement d’avant. Une panne reseau ne doit
  // refermer le gate sur personne, et le serveur reste juge au moment du Start
  // (`refusDeFenetre`).
  //
  // 🩸 Il se REMESURE, et ce n'est pas du zèle. La première version mesurait
  // une fois au montage et gardait la valeur pour la vie de la page — or une
  // page de cockpit vit des heures, et une horloge de poste, elle, bouge :
  // Windows la resynchronise, la machine dort et se réveille, un jeton échoue
  // au démarrage et la mesure avec. Payé le 09-09-2026 : la chatteuse du
  // Shift C, sur une page ouverte depuis 13:43, a vu son gate — et la présence
  // qui en dépend — basculer hors session à 14:30, soit soixante minutes pile
  // avant la fin réelle, sans avoir rechargé. Une mesure prise une fois est une
  // mesure qui ment dès que l'horloge change ; on la reprend au retour en
  // onglet visible, au retour du réseau, et toutes les cinq minutes. Une
  // mesure ratée garde la précédente : l’absence d’information n’est pas une
  // valeur.
  const horlogeDuServeur = useMutation(api.shiftGate.horlogeDuServeur);
  const [decalageHorlogeMs, setDecalageHorlogeMs] = useState(0);
  useEffect(() => {
    if (enDemo) return;
    let vivant = true;
    let enVol = false;
    const mesurer = () => {
      if (enVol || document.hidden) return;
      enVol = true;
      const avant = Date.now();
      void horlogeDuServeur({})
        .then(({ now: serveur }) => {
          if (!vivant) return;
          // Milieu de l’aller-retour : la latence se repartit entre les deux
          // sens, donc on compare au point median plutot qu’a l’un des bords.
          setDecalageHorlogeMs(serveur - (avant + Date.now()) / 2);
        })
        .catch(() => {
          // Injoignable, ou pas encore authentifié : on garde la mesure
          // précédente (0 s'il n'y en a pas), et la prochaine reprendra.
        })
        .finally(() => {
          enVol = false;
        });
    };
    mesurer();
    const periodique = setInterval(mesurer, HORLOGE_REMESURE_MS);
    const auRetour = () => {
      if (!document.hidden) mesurer();
    };
    document.addEventListener("visibilitychange", auRetour);
    window.addEventListener("online", auRetour);
    return () => {
      vivant = false;
      clearInterval(periodique);
      document.removeEventListener("visibilitychange", auRetour);
      window.removeEventListener("online", auRetour);
    };
  }, [horlogeDuServeur, enDemo]);

  // Tick local. visibilitychange pause/reprise pour éviter de re-render dans
  // le vide quand l'onglet n'est pas visible (mirror du tick principal).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    function start() {
      if (interval || document.hidden) return;
      interval = setInterval(() => setNow(Date.now()), GATE_TICK_MS);
    }
    function stop() {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    }
    function onVisibility() {
      if (document.hidden) stop();
      else {
        // Sync immédiate au retour pour éviter un compteur figé visiblement.
        setNow(Date.now());
        start();
      }
    }
    document.addEventListener("visibilitychange", onVisibility);
    start();
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return useMemo<ShiftGateHookResult>(() => {
    // Tout le calcul — et tous les compteurs qui en sortent — se fait sur
    // l’heure du serveur. Le tick local ne sert plus qu’a faire AVANCER le
    // temps, plus a dire lequel il est.
    const maintenant = now + decalageHorlogeMs;
    if (enDemo) {
      return {
        loading: false,
        state: etatDeLaPorte(shiftDemo, maintenant, lienVocalDemo ?? null),
        now: maintenant,
        hasAssignedShift: true,
        absences: AUCUNE_ABSENCE,
        decalageHorlogeMs,
        sessionOuverte: false,
        enPauseDeShift: false,
      };
    }
    if (gate === undefined)
      return {
        loading: true,
        state: null,
        now: maintenant,
        hasAssignedShift: false,
        absences: AUCUNE_ABSENCE,
        decalageHorlogeMs,
        sessionOuverte: false,
        enPauseDeShift: false,
      };
    const absences = gate.absences ?? AUCUNE_ABSENCE;
    return {
      loading: false,
      hasAssignedShift: gate.myShifts.length > 0,
      absences,
      sessionOuverte: gate.activeSession !== null,
      enPauseDeShift: gate.activeSession?.breakStartedAt != null,
      state: computeGateState({
        bypass: gate.bypass,
        orgHasShifts: gate.orgHasShifts,
        myShifts: gate.myShifts as ShiftBlock[],
        absences,
        activeSession: gate.activeSession,
        now: maintenant,
      }),
      now: maintenant,
      decalageHorlogeMs,
    };
  }, [
    enDemo,
    shiftDemo,
    lienVocalDemo,
    gate,
    now,
    decalageHorlogeMs,
  ]);
}
